export interface ResearchEntry {
  id: string;
  title: string;
  displayTitle: string;
  authors: string;
  date: string;
  status: string;
  category: string;
  tags: string[];
  abstract: string;
  asiAnalysis?: string;
  articleUrl?: string;
  sourceUrl?: string;
}

export const getSourceReferenceUrl = (entry: ResearchEntry) => {
  // ASI originals have no external source EXCEPT a minted DOI (e.g. Zenodo).
  return entry.sourceUrl ?? null;
};

export const isDoi = (url: string | null) => !!url && /doi\.org/.test(url);

export const researchLibrary: ResearchEntry[] = [
  {
    id: 'github-copilot-economic',
    title: 'The Economic Impact of the AI-Powered Developer Lifecycle',
    sourceUrl: 'https://arxiv.org/abs/2306.15033',
    displayTitle: 'GitHub Copilot Productivity Study: 55% Faster Coding and $1.5 Trillion GDP Impact',
    authors: 'GitHub / Keystone.AI / Harvard',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['developer-productivity', 'coding', 'economic-impact'],
    abstract: 'Controlled field experiments indicate that developers utilizing generative AI tools experience a dramatic acceleration in task completion. Developers complete tasks up to 55% faster when utilizing Copilot, alongside a 45% reduction in hours spent per code commit and a 59% decrease in external web searches for coding solutions. In files where GitHub Copilot is enabled, the tool completes approximately 46% of the code, with users accepting nearly 30% of code suggestions on average. Projections suggest that by increasing developer productivity by a conservative baseline of 30%, generative AI could effectively add the capacity of 15 million "effective developers" to the global workforce by 2030, potentially boosting global GDP by $1.5 trillion.',
  },
  {
    id: 'harvard-jagged-frontier',
    title: 'Navigating the Jagged Technological Frontier: Field Experimental Evidence of the Effects of AI on Knowledge Worker Productivity',
    sourceUrl: 'https://pubsonline.informs.org/doi/10.1287/orsc.2025.21838',
    displayTitle: 'The Jagged Frontier: Why AI Helps Some Tasks But Hurts Others',
    authors: 'Dell\'Acqua et al. (Harvard Business School / BCG)',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['consulting', 'task-performance', 'jagged-frontier'],
    abstract: 'This landmark study involving over 700 elite management consultants introduced the concept of the "Jagged Technological Frontier"—illustrating that AI capabilities do not scale linearly with human-perceived difficulty. For tasks falling strictly inside the frontier, consultants using GPT-4 completed 12.2% more tasks, completed them 25.1% faster, and produced results graded more than 40% higher in quality. Conversely, for tasks strategically designed to fall just outside the frontier, where the AI lacked sufficient logical reasoning but provided highly plausible yet incorrect outputs, consultants using AI were 19 percentage points less likely to produce correct solutions.',
  },
  {
    id: 'stanford-generative-ai-work',
    title: 'Generative AI at Work',
    sourceUrl: 'https://doi.org/10.1093/qje/qjae044',
    displayTitle: 'Does AI Help Junior or Senior Workers More? Stanford Study on the Shrinking Skill Gap',
    authors: 'Brynjolfsson, Li & Raymond (Stanford / NBER)',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['skill-gap', 'labor-economics', 'worker-performance'],
    abstract: 'This research indicates that generative AI systems implicitly capture and disseminate the best practices of high-performing workers, allowing newer or less skilled workers to move down the experience curve rapidly. AI provides disproportionate benefits to junior or lower-performing workers. This raises the baseline floor of productivity but flattens the overall skills distribution, requiring organizations to rethink compensation models, promotion tracks, and the traditional apprenticeship model of knowledge work.',
  },
  {
    id: 'microsoft-copilot-usage',
    title: 'What People Do With Copilot: The Copilot Usage Report 2025',
    sourceUrl: 'https://microsoft.ai/wp-content/uploads/2025/12/What_people_do_with_Copilot-8.pdf',
    displayTitle: 'Why Some Workers Get Huge Gains From AI While Others See Minimal Impact',
    authors: 'Microsoft Research',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['usage-patterns', 'behavioral-analysis', 'microsoft-copilot'],
    abstract: 'Analysis of 37.5 million deidentified interactions with Microsoft 365 Copilot reveals that AI usage depends fundamentally on context and device type. Temporal rhythms dictate integration: programming queries spike on weekdays, philosophical questions climb during late-night hours, and work-related queries dominate desktop usage precisely between 8 a.m. and 5 p.m. The data reveals deep variance in individual utilization that leads to asymmetric productivity outcomes across similar roles and organizations.',
  },

  // ENTERPRISE ADOPTION & IMPLEMENTATION FAILURE
  {
    id: 'gartner-genai-abandonment',
    title: 'Gartner Predicts 30% of Generative AI Projects Will Be Abandoned',
    sourceUrl: 'https://www.gartner.com/en/newsroom/press-releases/2024-07-29-gartner-predicts-30-percent-of-generative-ai-projects-will-be-abandoned-after-proof-of-concept-by-end-of-2025',
    displayTitle: 'Why 30-50% of Enterprise AI Projects Fail After Proof of Concept',
    authors: 'Gartner',
    date: '2024',
    status: 'Published',
    category: 'enterprise',
    tags: ['implementation', 'failure-rates', 'enterprise-adoption'],
    abstract: 'By the end of 2025, at least 30% of generative AI projects will be abandoned after the proof-of-concept phase. Primary catalysts for abandonment include poor data quality, inadequate risk controls, unclear business value, and costs that can quickly escalate to between $5 million and $20 million for large-scale enterprise deployments. Organizations frequently attempt to build advanced reasoning systems on top of fragmented, unstructured, and heavily siloed legacy data.',
  },
  {
    id: 'mckinsey-state-of-ai',
    title: 'The State of AI: Global Survey 2025',
    sourceUrl: 'https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai',
    displayTitle: 'Enterprise AI Adoption Statistics: 88% Use AI But Only 39% See Financial Impact',
    authors: 'McKinsey (QuantumBlack)',
    date: '2025',
    status: 'Published',
    category: 'enterprise',
    tags: ['enterprise-adoption', 'roi', 'survey'],
    abstract: 'By late 2025, 88% of organizations reported using AI regularly in at least one business function, up significantly from 78% in early 2025 and 55% in 2023. However, a severe disconnect exists between experimental deployment and scaled financial impact. While 74% of executives claim some form of return on investment (ROI) within the first 12 months, only 39% report a tangible impact on enterprise-level EBIT, and for most of those, the impact is less than 5% of their organization\'s total EBIT.',
  },
  {
    id: 'wharton-psychological-gap',
    title: 'Psychological Gap Causes AI Implementation to Fail',
    sourceUrl: 'https://executiveeducation.wharton.upenn.edu/thought-leadership/wharton-at-work/2026/04/a-solution-to-ai-adoption/',
    displayTitle: 'Why 40% of Gen Z Workers Resist or Sabotage AI Rollouts',
    authors: 'Puntoni (Wharton Human-AI Research)',
    date: '2025',
    status: 'Published',
    category: 'enterprise',
    tags: ['psychology', 'resistance', 'change-management'],
    abstract: 'Longitudinal research spanning 2023 to 2024 shows that 90% of senior decision-makers view AI as an opportunity to enhance employee skills. However, this top-down optimism clashes with reality on the ground. An estimated 40% of Generation Z workers actively resist or even subtly sabotage AI initiatives. Drawing on Self-Determination Theory, research shows that professionals resist AI systems that pose an existential threat to their professional identity. Resistance peaks when companies implement "black box" solutions that reduce human roles to peripheral tasks.',
  },
  {
    id: 'gartner-data-readiness',
    title: 'Lack of AI-Ready Data Puts AI Projects at Risk',
    sourceUrl: 'https://www.gartner.com/en/newsroom/press-releases/2025-02-26-lack-of-ai-ready-data-puts-ai-projects-at-risk',
    displayTitle: 'Why 60% of AI Projects Will Be Canceled Due to Data Issues',
    authors: 'Gartner',
    date: '2025',
    status: 'Published',
    category: 'enterprise',
    tags: ['data-readiness', 'infrastructure', 'risk'],
    abstract: 'By 2026, 60% of AI projects that are unsupported by AI-ready data will be canceled entirely. Organizations frequently attempt to build advanced reasoning systems on top of fragmented, unstructured, and heavily siloed legacy data. As Gartner analysts noted, "if the data has issues, then the data is not ready for AI."',
  },

  // LEGAL / HIGH-STAKES KNOWLEDGE WORK
  {
    id: 'aba-legal-tech',
    title: '2024 Legal Technology Survey Report',
    sourceUrl: 'https://www.americanbar.org/news/abanews/aba-news-archives/2025/03/aba-releases-survey-tech-trends/',
    displayTitle: 'AI in Law Firms: Document Review Now the Primary Use Case',
    authors: 'American Bar Association / Harvard Law',
    date: '2024',
    status: 'Published',
    category: 'legal',
    tags: ['legal', 'document-review', 'billable-hour'],
    abstract: '80% of legal professionals believe AI will have a high or transformational impact on their work within the next five years. "Saving time/increasing efficiency" was cited by 54.4% of respondents as the leading benefit of AI. Document review is projected to become the leading legal task within the year, with 65% of respondents planning to use generative AI for this purpose immediately, and 93% planning to use it within five years. However, this efficiency directly threatens the billable hour model that accounts for an estimated 80% of fee arrangements at major law firms.',
  },
  {
    id: 'stanford-legal-hallucinations',
    title: 'Hallucination-Free? Assessing the Reliability of Leading AI Legal Research Tools',
    sourceUrl: 'https://arxiv.org/abs/2405.20362',
    displayTitle: 'AI Hallucination Rates in Legal Research: Westlaw 34%, Lexis+ 17% Error Rate',
    authors: 'Magesh et al. (Stanford HAI & RegLab)',
    date: '2024',
    status: 'Published',
    category: 'legal',
    tags: ['legal', 'hallucinations', 'accuracy', 'rag'],
    abstract: 'A rigorous study evaluated the reliability of purpose-built legal AI tools including LexisNexis\'s Lexis+ AI and Thomson Reuters\' Westlaw AI-Assisted Research. Despite vendor claims of "hallucination-free" citations via Retrieval-Augmented Generation (RAG), Lexis+ AI produced incorrect information over 17% of the time, while Westlaw AI-Assisted Research hallucinated in more than 34% of cases. The underlying limitation is that legal retrieval is deeply semantic—LLMs struggle to distinguish between the "holding" (binding principle) and "dicta" (non-binding commentary), and fail to recognize when cited cases have been superseded.',
  },
  {
    id: 'aba-opinion-512',
    title: 'ABA Formal Opinion 512: Generative AI Tools',
    sourceUrl: 'https://www.americanbar.org/content/dam/aba/administrative/professional_responsibility/ethics-opinions/aba-formal-opinion-512.pdf',
    displayTitle: 'ABA Ethics Rules for Lawyers Using AI: Confidentiality and Competence Requirements',
    authors: 'American Bar Association',
    date: '2024',
    status: 'Published',
    category: 'infrastructure',
    tags: ['legal', 'ethics', 'compliance', 'confidentiality'],
    abstract: 'ABA Formal Opinion 512 establishes a firm ethical framework for AI deployment across the legal profession. Lawyers must exercise extreme vigilance regarding Model Rule 1.6 (Confidentiality), warning against inputting sensitive client data into public LLMs that lack enterprise-grade security agreements. Model Rule 1.1 (Competence) dictates that ignorance of AI technology is no longer a valid defense—attorneys have an ethical duty to understand the risks, mechanisms, and limitations of AI systems they utilize.',
  },
  {
    id: 'hipaa-ai-compliance',
    title: 'When AI Technology and HIPAA Collide',
    sourceUrl: 'https://www.hipaajournal.com/when-ai-technology-and-hipaa-collide/',
    displayTitle: 'HIPAA Compliance for AI in Healthcare: NIST Framework Integration',
    authors: 'HIPAA Journal / NIST',
    date: '2024',
    status: 'Published',
    category: 'infrastructure',
    tags: ['healthcare', 'hipaa', 'compliance', 'nist'],
    abstract: 'In the healthcare sector, HIPAA creates non-negotiable boundaries for AI data ingestion. If an AI technology pulls protected health information (PHI) from electronic health records, HIPAA regulations apply directly to the ingested data. The National Institute of Standards and Technology (NIST) AI Risk Management Framework (AI RMF) is now frequently deployed alongside HIPAA to manage the unique vulnerabilities of machine learning, focusing on principles such as validity, reliability, safety, and explainability.',
  },

  // BENCHMARKS & MULTI-MODEL SYSTEMS
  {
    id: 'llm-leaderboard-2025',
    title: 'LLM Leaderboard 2025',
    sourceUrl: 'https://www.vellum.ai/llm-leaderboard',
    displayTitle: 'GPT-5 vs Claude 4.5 vs Gemini 3: Which AI Tool Performs Best in 2025?',
    authors: 'Vellum AI / SWE-Bench',
    date: '2025',
    status: 'Published',
    category: 'benchmarks',
    tags: ['benchmarks', 'model-comparison', 'coding', 'performance'],
    abstract: 'AI model performance is highly task-dependent; no single model universally dominates. For deep reasoning, complex logic, and agentic coding (measured by SWE-bench metrics), Anthropic\'s Claude 4.5 and 4.6 variants achieve scores ranging from 79.6% to 82%, outperforming GPT-5.2\'s 74.9% to 80%. OpenAI\'s GPT-5.3 Codex has dropped Time To First Token (TTFT) to 0.003 seconds, making it premier for real-time applications. Google\'s Gemini 3 Pro excels in massive context window retrieval and quantitative reasoning, scoring 100% on high school math benchmarks.',
  },
  {
    id: 'mount-sinai-multi-agent',
    title: 'Orchestrated Multi Agents Sustain Accuracy',
    sourceUrl: 'https://www.nature.com/articles/s44401-026-00077-0',
    displayTitle: 'Multi-Agent AI Systems Use 65x Less Resources Than Single Models',
    authors: 'Klang et al. (Mount Sinai / NPJ Health Systems)',
    date: '2026',
    status: 'Published',
    category: 'benchmarks',
    tags: ['multi-agent', 'efficiency', 'clinical-ai', 'orchestration'],
    abstract: 'A landmark study evaluating clinical workloads demonstrated that a coordinated multi-agent system maintained superior diagnostic accuracy while consuming up to 65 times fewer computing resources than a massive single-agent design. By routing diverse data types (text, images, sound) to specialized models in parallel, the system prevented the breakdown in accuracy that typically plagues single models under heavy real-world clinical traffic.',
  },
  {
    id: 'google-scaling-agents',
    title: 'Towards a Science of Scaling Agent Systems',
    sourceUrl: 'https://arxiv.org/abs/2512.08296',
    displayTitle: 'When Do More AI Agents Help vs Hurt Performance?',
    authors: 'Google Research',
    date: '2025',
    status: 'Published',
    category: 'benchmarks',
    tags: ['multi-agent', 'scaling', 'orchestration', 'google'],
    abstract: 'A comprehensive study evaluated 180 distinct agent configurations to challenge the heuristic that "more agents are always better." The research derived quantitative scaling principles revealing that multi-agent coordination dramatically improves performance on parallelizable tasks, but can actually degrade performance on sequential ones. Intelligent, context-aware routing—rather than simply inflating the parameter count of a single model—is the definitive key to enterprise AI efficiency.',
  },

  // LABOR MARKET DYNAMICS
  {
    id: 'pwc-ai-jobs-barometer',
    title: 'Global AI Jobs Barometer 2025',
    sourceUrl: 'https://www.pwc.com/gx/en/issues/artificial-intelligence/job-barometer/2025/report.pdf',
    displayTitle: 'AI Skills Salary Premium: Workers With AI Skills Earn 56% More',
    authors: 'PwC / Lightcast / Burtch Works',
    date: '2025',
    status: 'Published',
    category: 'labor-market',
    tags: ['salary', 'skills-premium', 'labor-market', 'compensation'],
    abstract: 'Workers with AI skills command an average wage premium of 56% over peers in the exact same occupation lacking those skills—more than double the 25% premium recorded in 2023. Industries deemed "most exposed" to AI adoption experienced a fourfold increase in productivity growth—rising from 7% to 27%—and a 27% growth in revenue per employee. This exponential salary premium is justified directly by corporate output differentials.',
  },
  {
    id: 'raleigh-tech-talent',
    title: 'Scoring Tech Talent 2025 / NC Tech Industry Report',
    sourceUrl: 'https://www.cbre.com/insights/books/scoring-tech-talent-2025',
    displayTitle: 'Research Triangle AI Job Growth: 15.4% Tech Workforce Expansion',
    authors: 'CBRE / NC Tech Association',
    date: '2025',
    status: 'Published',
    category: 'labor-market',
    tags: ['raleigh-durham', 'research-triangle', 'regional-growth'],
    abstract: 'Raleigh-Durham (Research Triangle) ranks #12 among all North American tech talent markets, experiencing 15.4% growth in tech workforce between 2021 and 2024. The region now boasts over 76,570 tech workers, and AI engineers in North Carolina command salaries averaging between $110,000 and $138,000. Tech accounts for 6.7% of total jobs statewide, but the average pay of $148,000 is more than double the statewide average of $70,700.',
  },
  {
    id: 'ai-graduate-jobs',
    title: 'AI\'s Impact on Graduate Jobs: A 2025 Data Analysis',
    sourceUrl: 'https://intuitionlabs.ai/articles/ai-impact-graduate-jobs-2025',
    displayTitle: 'Is AI Replacing Entry-Level Jobs? 13% Decline in Early-Career Employment',
    authors: 'IntuitionLabs (Stanford Data) / World Economic Forum',
    date: '2025',
    status: 'Published',
    category: 'labor-market',
    tags: ['entry-level', 'graduates', 'job-displacement', 'early-career'],
    abstract: 'In occupations heavily exposed to generative AI, early-career workers (ages 22-25) have already seen a 13% relative decline in overall employment. The UK tech industry reported a 46% cut in traditional graduate roles from 2023 to 2024. IDC surveys report that 66% of enterprise firms are actively reducing entry-level hiring directly due to AI capabilities. Employers are increasingly stripping traditional degree requirements in favor of demonstrated, portfolio-based AI competency.',
  },

  // AI RESEARCH DATA REQUEST - ADDITIONAL STUDIES
  {
    id: 'external-101',
    title: 'Experimental Evidence on the Productivity Effects of Generative Artificial Intelligence',
    sourceUrl: 'https://doi.org/10.1126/science.adh2586',
    displayTitle: 'Experimental Evidence on the Productivity Effects of Generative Artificial Intelligence',
    authors: 'Noy, S., & Zhang, W.',
    date: 'July 2023',
    status: 'Published',
    category: 'productivity',
    tags: ['productivity', 'llm'],
    abstract: 'In a randomized trial of 453 professionals, ChatGPT decreased writing task time by 0.8 standard deviations (a 37% or 10-minute reduction) and increased quality by 0.4 standard deviations. The tool significantly compressed the productivity distribution, benefiting low-ability workers the most while restructuring task time away from rough-drafting. Exposure increased job satisfaction but also heightened automation anxieties among workers.',
    
  },
  {
    id: 'external-104',
    title: 'Estimating AI productivity gains from Claude conversations',
    sourceUrl: 'https://www.anthropic.com/research/estimating-productivity-gains',
    displayTitle: 'Estimating AI productivity gains from Claude conversations',
    authors: 'Anthropic Economic Research Team',
    date: 'Nov 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['productivity'],
    abstract: 'Utilizing a privacy-preserving analysis of 100,000 real-world conversations, Claude models estimated that AI reduces task completion time by 80% for complex tasks that traditionally require 1.4 hours. Extrapolating these task-level time savings suggests current-generation AI could increase US labor productivity growth by 1.8% annually over the next decade. The researchers note this projection is dependent on widespread enterprise adoption rates.',
  },
  {
    id: 'external-105',
    title: 'The Economic Potential of Generative AI: The Next Productivity Frontier',
    sourceUrl: 'https://www.mckinsey.com/capabilities/tech-and-ai/our-insights/the-economic-potential-of-generative-ai-the-next-productivity-frontier',
    displayTitle: 'The Economic Potential of Generative AI: The Next Productivity Frontier',
    authors: 'Chui, M., et al.',
    date: 'June 2023',
    status: 'Published',
    category: 'labor-economics',
    tags: ['productivity', 'llm'],
    abstract: 'The report estimates that applying Generative AI could increase customer care productivity by a value of 30% to 45% of current function costs, and marketing productivity by 5% to 15% of total spending. Globally, the emergence of these tools increased the estimate of worker activities that have the technical potential to be automated from 50% to between 60% and 70%. The banking sector alone could generate an additional $200 billion to $340 billion in value.',
  },
  {
    id: 'external-106',
    title: 'Seven Myths About AI and Productivity: What the Evidence Really Says',
    sourceUrl: 'https://cmr.berkeley.edu/2025/10/seven-myths-about-ai-and-productivity-what-the-evidence-really-says/',
    displayTitle: 'Seven Myths About AI and Productivity: What the Evidence Really Says',
    authors: 'Huidobro, J. O., García-Castro, R., & Munoz, J. M.',
    date: 'Oct 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['productivity'],
    abstract: 'This meta-analysis debunks the assumption of universal value, finding no robust relationship between AI adoption and aggregate macroeconomic productivity gains. The evidence reveals that human-AI collaboration frequently underperforms isolated human or AI agents, except in highly narrow creative tasks. The study calls for a shift away from technological hype toward context-specific organizational deployment strategies to capture genuine value.',
  },
  {
    id: 'external-107',
    title: 'Artificial Intelligence, Productivity, and the Workforce: Evidence from Corporate Executives',
    sourceUrl: 'https://www.atlantafed.org/-/media/Project/Atlanta/FRBA/Documents/research/publication/working-paper/2026/03/25/04-artificial-intelligence-productivity-and-the-workforce.pdf',
    displayTitle: 'Artificial Intelligence, Productivity, and the Workforce: Evidence from Corporate Executives',
    authors: 'Babina, T., Fedyk, A., He, A. X., & Hodson, J.',
    date: 'March 2026',
    status: 'Published',
    category: 'labor-economics',
    tags: ['productivity', 'labor-economics'],
    abstract: 'This empirical analysis finds that a substantial share of AI spending takes the form of operating expenses (e.g., SaaS subscriptions) rather than capitalized investment, operating through channels not captured by standard capital deepening. Consequently, the direct near-term effects of generative AI on labor demand could be negative. This highlights a critical disconnect between technological capability and measurable macroeconomic growth.',
  },
  {
    id: 'external-108',
    title: 'AI, Productivity, and Labor Markets: A Review of the Empirical Evidence',
    sourceUrl: 'https://laweconcenter.org/resources/ai-productivity-and-labor-markets-a-review-of-the-empirical-evidence/',
    displayTitle: 'AI, Productivity, and Labor Markets: A Review of the Empirical Evidence',
    authors: 'Fruits, E., & Stout, K.',
    date: 'Feb 2026',
    status: 'Published',
    category: 'labor-economics',
    tags: ['productivity', 'labor-economics'],
    abstract: 'Field experiments involving nearly 5,000 software developers show AI access increased weekly task completion by 26.08%. However, broader administrative data reveals a 16% employment decline relative to trend for workers ages 22-25 in highly exposed occupations following ChatGPT\'s release. This confirms that massive task-level productivity gains exist alongside targeted displacement of junior personnel within adopting firms.',
  },
  {
    id: 'external-109',
    title: 'The state of AI in 2025: Agents, innovation, and transformation',
    sourceUrl: 'https://www.mckinsey.com/capabilities/quantumblack/our-insights/the-state-of-ai',
    displayTitle: 'The state of AI in 2025: Agents, innovation, and transformation',
    authors: 'QuantumBlack AI',
    date: 'Nov 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'While 88% of organizations report using AI, 66% remain stuck in the experimentation phase and have not scaled the technology enterprise-wide. Only 39% attribute any EBIT impact to AI, and for the vast majority, this impact accounts for less than 5% of total EBIT. Alarmingly, 51% of adopting organizations experienced negative consequences, primarily driven by output inaccuracy and workflow misalignment.',
  },
  {
    id: 'external-110',
    title: 'The GenAI Divide: State of AI in Business 2025',
    sourceUrl: 'https://mlq.ai/media/quarterly_decks/v0.1_State_of_AI_in_Business_2025_Report.pdf',
    displayTitle: 'The GenAI Divide: State of AI in Business 2025',
    authors: 'Project NANDA',
    date: 'July 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'The study reveals that roughly 95% of enterprise generative AI pilots fail to scale or deliver a measurable P&L impact, despite an estimated $30–40 billion in enterprise investment. For embedded, task-specific GenAI, 60% of organizations evaluate tools, only 20% reach the pilot stage, and a mere 5% successfully achieve production. Furthermore, internal AI builds fail twice as often as those involving specialized external partnerships.',
  },
  {
    id: 'external-111',
    title: 'The Root Causes of Failure for Artificial Intelligence Projects and How They Can Succeed',
    sourceUrl: 'https://doi.org/10.7249/PTA2680-1',
    displayTitle: 'The Root Causes of Failure for Artificial Intelligence Projects and How They Can Succeed',
    authors: 'Ryseff, J., & Narayanan, A.',
    date: 'Aug 2024',
    status: 'Published',
    category: 'enterprise',
    tags: ['ai-research'],
    abstract: 'Based on structured interviews with experienced engineers, the research demonstrates that over 80% of AI/ML projects fail to reach meaningful production, double the failure rate of traditional IT initiatives. The leading root causes are misunderstood problem definitions, an absence of the necessary high-quality data to train effective models, and a "technology-first" rather than "problem-first" mentality among executive leadership.',
    
  },
  {
    id: 'external-112',
    title: 'Gartner Predicts 30% of Generative AI Projects Will Be Abandoned After Proof of Concept By End of 2025',
    sourceUrl: 'https://www.gartner.com/en/newsroom/press-releases/2024-07-29-gartner-predicts-30-percent-of-generative-ai-projects-will-be-abandoned-after-proof-of-concept-by-end-of-2025',
    displayTitle: 'Gartner Predicts 30% of Generative AI Projects Will Be Abandoned After Proof of Concept By End of 2025',
    authors: 'Sallam, R.',
    date: 'July 2024',
    status: 'Published',
    category: 'productivity',
    tags: ['llm'],
    abstract: 'Gartner predicts at least 30% of GenAI projects will be entirely abandoned post-POC due to poor data quality, inadequate risk controls, and escalating costs. Deployment costs for transformative models range unpredictably from $5 million to $20 million. CFOs increasingly struggle to justify these massive financial burdens against indirect productivity metrics, leading to rapid project cancellation when immediate ROI fails to materialize.',
  },
  {
    id: 'external-113',
    title: 'Middle Market AI Survey 2025: U.S. and Canada',
    sourceUrl: 'https://rsmus.com/insights/services/digital-transformation/rsm-middle-market-ai-survey-2025.html',
    displayTitle: 'Middle Market AI Survey 2025: U.S. and Canada',
    authors: 'Beyer, R., et al.',
    date: 'June 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'While 91% of mid-market companies have adopted GenAI, an overwhelming 92% encountered significant implementation challenges, with 62% stating the process was far harder than expected. Data quality was cited by 41% of struggling firms as the primary technical obstacle, while 39% cited a critical lack of in-house expertise. Consequently, 70% of respondents admitted requiring outside assistance to maximize AI potential.',
  },
  {
    id: 'external-114',
    title: 'The AI Executive\'s Handbook: Harness the Ungoverned Machine',
    sourceUrl: 'https://www.amazon.com/AI-Executive-Harnessing-Ungoverned-Generative/dp/B0FX2GMHCG',
    displayTitle: 'The AI Executive\'s Handbook: Harness the Ungoverned Machine',
    authors: 'Sulpovar, M.',
    date: 'Oct 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'This text identifies the widespread failure mode of "Adoption by Drift," warning that shadow AI creates uncoordinated, ungoverned pilots without enterprise guardrails. It asserts that internal implementations will continue to fail to generate ROI until intelligence is intentionally treated as governed infrastructure via a centralized "AI Control Plane," shifting from localized novelty to auditable value creation.',
  },
  {
    id: 'external-115',
    title: 'The Production AI Reality Check: Why 80% of AI Projects Fail to Reach Production',
    sourceUrl: 'https://medium.com/@archie.kandala/the-production-ai-reality-check-why-80-of-ai-projects-fail-to-reach-production-849daa80b0f3',
    displayTitle: 'The Production AI Reality Check: Why 80% of AI Projects Fail to Reach Production',
    authors: 'Kandala, A.',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Highlighting infrastructure deficits, this report reveals that 74% of companies are dissatisfied with current GPU scheduling tools, and only 15% achieve greater than 85% GPU utilization during peak periods. Between 34% and 53% of mature organizations cite a lack of specialized AI infrastructure talent as their primary deployment obstacle, leading to systemic failures resembling the multi-billion dollar collapse of IBM Watson for Oncology.',
  },
  {
    id: 'external-116',
    title: 'Enterprise AI Implementation Failure: Root Causes and Structural Alternatives',
    sourceUrl: 'https://talyx.ai/insights/enterprise-ai-implementation-failure',
    displayTitle: 'Enterprise AI Implementation Failure: Root Causes and Structural Alternatives',
    authors: 'Talyx AI Research',
    date: '2025',
    status: 'Published',
    category: 'enterprise',
    tags: ['enterprise'],
    abstract: 'Aggregating data across multiple institutional sources, this analysis confirms a 70% to 85% enterprise implementation failure rate, making failure the statistically dominant outcome of AI investment. It underscores that only approximately 5% of AI pilot programs achieve rapid revenue acceleration. Crucially, it finds that purchasing specialized vendor solutions succeeds 67% of the time, compared to a mere 33% success rate for internal corporate builds.',
  },
  {
    id: 'external-117',
    title: 'Investigating Whether AI Will Replace Human Physicians... Randomized Factorial Experiment',
    sourceUrl: 'https://doi.org/10.2196/66760',
    displayTitle: 'Investigating Whether AI Will Replace Human Physicians... Randomized Factorial Experiment',
    authors: 'Guo, W., & Chen, Y.',
    date: 'Mar 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'A 249-participant factorial experiment tracking patient evaluation of AI versus human-involved diagnoses. It found that despite promises of technological efficiency and high accuracy, AI does not inherently improve patient confidence, relational engagement, or adherence at the emotional level. The study proves that AI as a functional technological solution fails to replicate meaningful human relational interactions.',
    
  },
  {
    id: 'external-118',
    title: 'Psychological traits on trust in and reliance on AI advice in classification tasks',
    sourceUrl: 'https://doi.org/10.1080/10447318.2024.2348216',
    displayTitle: 'Psychological traits on trust in and reliance on AI advice in classification tasks',
    authors: 'Küper, L., et al.',
    date: '2024',
    status: 'Published',
    category: 'cognitive-science',
    tags: ['cognitive-science'],
    abstract: 'An experimental mixed-methods study (N=250) proving that user personality traits significantly dictate instances of harmful over- and under-reliance on AI. The data shows novices exhibited higher trust and greater reliance tendencies, whereas experts demonstrated greater self-confidence and lower trust in automation, underscoring the need for personalized trust calibration in UI design.',
    
  },
  {
    id: 'external-119',
    title: 'Overreliance on Large Language Models',
    sourceUrl: 'https://doi.org/10.48550/arXiv.2509.08010',
    displayTitle: 'Overreliance on Large Language Models',
    authors: 'Schemmer, M., et al.',
    date: 'Sept 2024',
    status: 'Published',
    category: 'cognitive-science',
    tags: ['ai-research'],
    abstract: 'This paper redefines overreliance in LLMs not as a single-shot decision, but as a cognitive state that unfolds over continuous, iterative prompting. The researchers highlight that the general-purpose fluency of LLMs creates an "illusion of universal competence," exacerbating anthropomorphism and making it nearly impossible for users to accurately gauge the machine\'s domain-specific limitations.',
    
  },
  {
    id: 'external-120',
    title: 'Automation bias affecting physicians\' diagnostic reasoning when using ChatGPT-4o',
    sourceUrl: 'https://doi.org/10.1101/2025.08.23.25334280',
    displayTitle: 'Automation bias affecting physicians\' diagnostic reasoning when using ChatGPT-4o',
    authors: 'medRxiv (Preprint Researchers)',
    date: 'Aug 2025',
    status: 'Published',
    category: 'cognitive-science',
    tags: ['llm'],
    abstract: 'A single-blind randomized clinical trial demonstrating that exposure to erroneous LLM recommendations significantly degraded the diagnostic performance of AI-trained physicians. The fluency of the AI induced severe automation bias, proving that even highly educated specialists succumb to over-reliance when presented with confidently delivered, yet hallucinated, medical advice.',
    
  },
  {
    id: 'external-121',
    title: 'Stimulating Cognitive Engagement in Hybrid Decision-Making: Friction, Reliance, and Biases',
    sourceUrl: 'https://www.grin-informatica.it/call-for-abstracts-second-workshop-on-stimulating-cognitive-engagement-in-hybrid-decision-making-friction-reliance-and-biases-june-10-2025-pisa-italy-co-located-with-hhai25/',
    displayTitle: 'Stimulating Cognitive Engagement in Hybrid Decision-Making: Friction, Reliance, and Biases',
    authors: 'HHAI Workshop Organizers',
    date: 'June 2025',
    status: 'Published',
    category: 'cognitive-science',
    tags: ['cognitive-science'],
    abstract: 'This research advocates for "friction-in-design" to combat systemic cognitive offloading. The findings show that deliberately slowing down user decisions—such as requiring written justifications before accepting AI recommendations or displaying complex uncertainty visualizations—effectively reinforces human oversight and mitigates automated compliance.',
  },
  {
    id: 'external-122',
    title: 'The Effect of Explainable AI on Automation Bias and Over-Reliance in High-Risk Decision Systems',
    sourceUrl: 'https://www.researchgate.net/publication/401428408_The_Effect_of_Explainable_AI_on_Automation_Bias_and_Over-Reliance_in_High-Risk_Decision_Systems',
    displayTitle: 'The Effect of Explainable AI on Automation Bias and Over-Reliance in High-Risk Decision Systems',
    authors: 'Idika, S., et al.',
    date: 'Mar 2026',
    status: 'Published',
    category: 'cognitive-science',
    tags: ['ai-research'],
    abstract: 'Investigates high-stakes environments, finding that without Explainable AI (XAI), human vigilance drops precipitously, leading to severe errors and diminished accountability. Implementing feature attribution methods (SHAP/LIME) and counterfactual explanations is absolutely required to empower users to critically evaluate AI logic rather than blindly trusting the output.',
  },
  {
    id: 'external-123',
    title: 'Loss of Human Agency in AI Decision Workflows',
    sourceUrl: 'https://www.mdpi.com/3042-8130/1/1/3',
    displayTitle: 'Loss of Human Agency in AI Decision Workflows',
    authors: 'MDPI Systems Review',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'In evaluated enterprise scenarios, a staggering 78% of human agents defaulted to AI-generated decisions without conducting any critical review. This total loss of agency was driven by automation bias (accounting for 51% of decisions), technology superiority bias (19%), and confirmation bias (8%), proving that human-in-the-loop is often a functional illusion.',
  },
  {
    id: 'external-124',
    title: 'Gen-AI: Artificial Intelligence and the Future of Work',
    sourceUrl: 'https://www.imf.org/-/media/files/publications/sdn/2024/english/sdnea2024001.pdf',
    displayTitle: 'Gen-AI: Artificial Intelligence and the Future of Work',
    authors: 'Cazzaniga, M., et al.',
    date: 'Jan 2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Finds that 40% of global employment is exposed to AI. In Advanced Economies, 27% of jobs face high-complementarity exposure (raising top wages), while 33% face low-complementarity exposure. The report warns this will cause massive polarization and rising income inequality unless governments implement robust digital upskilling and transition policies.',
  },
  {
    id: 'external-125',
    title: 'The Potentially Large Effects of Artificial Intelligence on Economic Growth',
    sourceUrl: 'https://www.gspublishing.com/content/research/en/reports/2023/03/27/d64e052b-0f6e-45d7-967b-d7be35fabd16.html',
    displayTitle: 'The Potentially Large Effects of Artificial Intelligence on Economic Growth',
    authors: 'Briggs, J., & Kodnani, D.',
    date: 'March 2023',
    status: 'Published',
    category: 'labor-economics',
    tags: ['ai-research'],
    abstract: 'Projects that 300 million full-time jobs globally are exposed to automation, representing 18% of all work. In the US and EU, 66% of jobs have exposure, with AI capable of substituting up to 25% of current tasks. Widespread adoption could eventually increase annual global GDP by 7% (nearly $7 trillion) over a 10-year period.',
  },
  {
    id: 'external-126',
    title: 'The Simple Macroeconomics of AI',
    sourceUrl: 'https://www.nber.org/papers/w32487',
    displayTitle: 'The Simple Macroeconomics of AI',
    authors: 'Acemoglu, D.',
    date: 'May 2024',
    status: 'Published',
    category: 'labor-economics',
    tags: ['ai-research'],
    abstract: 'Argues against unchecked technological optimism, positing that higher productivity and the deliberate creation of new, labor-intensive jobs are absolutely necessary to support the labor share of income. Cautions that rapid, unmanaged automation without concurrent new task creation will displace workers much faster than it creates opportunities.',
  },
  {
    id: 'external-127',
    title: 'OECD Employment Outlook 2024',
    sourceUrl: 'https://www.oecd.org/en/publications/oecd-employment-outlook-2024_ac8b3538-en.html',
    displayTitle: 'OECD Employment Outlook 2024',
    authors: 'OECD',
    date: 'July 2024',
    status: 'Published',
    category: 'labor-economics',
    tags: ['ai-research'],
    abstract: 'Identifies a massive structural "retraining gap" required to transition the modern workforce. Surveys show 64% to 71% of existing workers require immediate retraining or upskilling to remain viable, yet 35% to 48% of firms indicate a preference to simply hire new, natively skilled workers rather than invest in transitioning their existing employee base.',
  },
  {
    id: 'external-128',
    title: 'Measuring US workers\' capacity to adapt to AI-driven job displacement',
    sourceUrl: 'https://www.brookings.edu/articles/measuring-us-workers-capacity-to-adapt-to-ai-driven-job-displacement/',
    displayTitle: 'Measuring US workers\' capacity to adapt to AI-driven job displacement',
    authors: 'Brookings Institution Researchers',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Tracks specific AI exposure by occupation, finding that General Office Clerks face 50% exposure (impacting 2.5 million jobs), Medical Secretaries face 63% exposure (831,000 jobs), and Interpreters/Translators face a massive 82% exposure rate. The data underscores the necessity for unprecedented occupational mobility infrastructure.',
  },
  {
    id: 'external-129',
    title: 'Research on AI and the labor market is still in the first inning',
    sourceUrl: 'https://www.brookings.edu/articles/new-data-show-no-ai-jobs-apocalypse-for-now/',
    displayTitle: 'Research on AI and the labor market is still in the first inning',
    authors: 'Anthropic / Brookings',
    date: '2024',
    status: 'Published',
    category: 'labor-economics',
    tags: ['labor-economics'],
    abstract: 'Utilizing API usage data from Anthropic\'s Claude model, the analysis reveals a stark divergence in intent. While consumer chatbot use is split evenly for augmentation versus automation, an overwhelming 77% of enterprise API tasks are deployed explicitly for the purpose of absolute task automation, signaling aggressive corporate cost-cutting motives.',
  },
  {
    id: 'external-131',
    title: 'The Impact of Artificial Intelligence on Unemployment: A Review',
    sourceUrl: 'https://doi.org/10.1108/IJSE-05-2023-0338',
    displayTitle: 'The Impact of Artificial Intelligence on Unemployment: A Review',
    authors: 'Virgilio, G., et al.',
    date: '2024',
    status: 'Published',
    category: 'labor-economics',
    tags: ['ai-research'],
    abstract: 'Confirms that while individual firms adopting automation expand their overall employment to outcompete rivals, this growth comes directly at the expense of non-adopting competitors. This dynamic drives aggregate wage inequality and shifts the total burden of job loss and firm closure onto technologically lagging companies.',
    
  },
  {
    id: 'external-132',
    title: 'Large Legal Fictions: Profiling Legal Hallucinations in Large Language Models',
    sourceUrl: 'https://arxiv.org/abs/2401.01301',
    displayTitle: 'Large Legal Fictions: Profiling Legal Hallucinations in Large Language Models',
    authors: 'Dahl, M., Magesh, V., Suzgin, M., & Ho, D.E.',
    date: '2024',
    status: 'Published',
    category: 'professional-services',
    tags: ['legal'],
    abstract: 'LLMs hallucinate verifiable federal court cases at exceptionally high rates: GPT-4 (58%), GPT-3.5 (69%), PaLM 2 (72%), and Llama 2 (88%). Hallucinations increase severely for lower federal courts and less prominent cases due to distributional bias in training data, and models frequently exhibit contra-factual bias by inventing fake precedents to agree with users.',
  },
  {
    id: 'external-133',
    title: 'Evaluating LLMs for medical diagnosis accuracy vs human physicians',
    sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12190018/',
    displayTitle: 'Evaluating LLMs for medical diagnosis accuracy vs human physicians',
    authors: 'SSM Study Cohort',
    date: '2024',
    status: 'Published',
    category: 'professional-services',
    tags: ['llm', 'healthcare'],
    abstract: 'Across extensive evaluations involving Italian and French physician cohorts, AI diagnostic accuracy ranged from 72% to 96% on clinical evaluations. This drastically outperformed the participating human physicians, whose accuracy ranged significantly lower, between 46% and 62%, proving the immense power of models in bounded diagnostic parameters.',
  },
  {
    id: 'external-134',
    title: 'DiagnosticXchange: Evaluating dynamic clinical diagnosis using AI',
    sourceUrl: 'https://www.medrxiv.org/content/10.1101/2025.08.13.25333529v1',
    displayTitle: 'DiagnosticXchange: Evaluating dynamic clinical diagnosis using AI',
    authors: 'Specialist Multi-agent researchers',
    date: 'Aug 2025',
    status: 'Published',
    category: 'professional-services',
    tags: ['ai-research'],
    abstract: 'Utilizing 16 complex neurological diagnostic challenges, this study proved static benchmarks fail to capture clinical reality. Models were tested on dynamic procedural cost efficiency, time to diagnosis, and iterative hypothesis generation, showing multi-agent systems can strategically select high-value tests superior to average human baselines.',
    
  },
  {
    id: 'external-135',
    title: 'Investigating Whether AI Will Replace Human Physicians...',
    sourceUrl: 'https://doi.org/10.2196/66760',
    displayTitle: 'Investigating Whether AI Will Replace Human Physicians...',
    authors: 'Guo, W., & Chen, Y.',
    date: 'Mar 2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'While recognizing high diagnostic accuracy, this study of 249 participants found AI categorically fails to improve patient confidence, emotional support, and treatment adherence. The data proves that functional diagnostic accuracy alone cannot replace human relational care, firmly positioning AI as an augmenting tool rather than a physician replacement.',
    
  },
  {
    id: 'external-136',
    title: '2025 GenAI in Professional Services and Accounting Report',
    sourceUrl: 'https://www.cpa.com/news/cpacom-issues-2025-ai-accounting-report',
    displayTitle: '2025 GenAI in Professional Services and Accounting Report',
    authors: 'CPA.com / be radical',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'The report declares AI in accounting has shifted from an experiment to core operational infrastructure. Among firms actively using GenAI, 44% use it multiple times a day. Crucially, 83% of anti-fraud professionals plan to integrate GenAI within two years specifically to catch anomalies that traditional manual audits inevitably miss.',
  },
  {
    id: 'external-137',
    title: 'Human + AI in Accounting: Early Evidence from the Field',
    sourceUrl: 'https://www.gsb.stanford.edu/insights/ai-reshaping-accounting-jobs-doing-boring-stuff',
    displayTitle: 'Human + AI in Accounting: Early Evidence from the Field',
    authors: 'Choi, J.H., & Xie, C.',
    date: 'July 2023',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Field evidence shows nearly 50% of accountants reported AI directly helped them meet deadlines and improve accuracy. 66% noted that automating routine bookkeeping tasks was the single primary benefit, though complex areas like audit validation and strategic tax valuation remain largely untouched and require human judgment.',
  },
  {
    id: 'external-138',
    title: 'AI Benefits in Accounting / 2024 Anti-Fraud Benchmarking',
    sourceUrl: 'https://www.dualentry.com/blog/ai-benefits-in-accounting',
    displayTitle: 'AI Benefits in Accounting / 2024 Anti-Fraud Benchmarking',
    authors: 'ACFE / SAS',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Demonstrates early adoption trends, noting 18% of anti-fraud professionals already actively use AI/ML. Automated anomaly detection successfully identifies systemic issues traditional manual audits overlook, actively shifting the modern accountant\'s role from a basic "bean counter" to a highly leveraged strategic business partner.',
  },
  {
    id: 'external-139',
    title: 'The Impact of Artificial Intelligence on the Reading Times of Radiologists',
    sourceUrl: 'https://doi.org/10.1038/s41746-023-00829-4',
    displayTitle: 'The Impact of Artificial Intelligence on the Reading Times of Radiologists',
    authors: 'Shin, H.J., et al.',
    date: '2023',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Demonstrates that the integration of AI models in analyzing chest radiographs significantly improves the diagnostic speed of radiologists while maintaining or actively improving diagnostic accuracy. This proves high operational viability for bounded, visual-pattern recognition tasks where ground truth is visually verifiable.',
    
  },
  {
    id: 'external-140',
    title: 'MMLU-Pro: A More Robust and Challenging Multi-Task Language Understanding Benchmark',
    sourceUrl: 'https://doi.org/10.48550/arXiv.2406.01574',
    displayTitle: 'MMLU-Pro: A More Robust and Challenging Multi-Task Language Understanding Benchmark',
    authors: 'Wang, Y., et al.',
    date: 'June 2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Replaces the saturated MMLU with a 12,032-question dataset featuring 10 options per question instead of 4. Tested frontier models saw a massive 16-33% accuracy drop (e.g., GPT-4o dropped from 88.7% to 72.6%), proving previous benchmarks vastly overestimated reasoning capabilities due to the statistical probability of lucky guessing.',
    
  },
  {
    id: 'external-141',
    title: 'GPQA: A Graduate-Level Google-Proof Q&A Benchmark',
    sourceUrl: 'https://arxiv.org/abs/2311.12022',
    displayTitle: 'GPQA: A Graduate-Level Google-Proof Q&A Benchmark',
    authors: 'Rein, D., et al.',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Features 448 expert-written, PhD-level physics, biology, and chemistry questions. Human domain experts score 65%, non-expert human validators with unrestricted web access score 34%, while current top AI models hover around 59.4% to 83%, highlighting deep, legitimate capability ceilings in cutting-edge scientific reasoning.',
  },
  {
    id: 'external-142',
    title: 'Contamination Detection via output Distribution (CDD)',
    sourceUrl: 'https://arxiv.org/abs/2402.15938',
    displayTitle: 'Contamination Detection via output Distribution (CDD)',
    authors: 'ACL Researchers',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Introduces a novel black-box method detecting training data contamination by mathematically analyzing the peakedness of an LLM\'s output distributions. Correcting for this contamination using the companion TED method reduces artificially inflated performance metrics by up to an astounding 66.9%.',
  },
  {
    id: 'external-143',
    title: 'Time Travel in LLMs: Detecting Contamination',
    sourceUrl: 'https://arxiv.org/abs/2308.08493',
    displayTitle: 'Time Travel in LLMs: Detecting Contamination',
    authors: 'ICLR Researchers',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['llm'],
    abstract: 'Utilizes a "guided instruction" method to detect if an LLM was secretly trained on test data by providing a dataset name and initial segment, then asking the model to complete it. This forensic approach achieves 92% to 100% detection accuracy for identifying data contamination in black-box models.',
  },
  {
    id: 'external-144',
    title: 'Understanding the Limitations of Mathematical Reasoning in Large Language Models',
    sourceUrl: 'https://securing.dev/posts/ai-security-reasoning-and-bias/',
    displayTitle: 'Understanding the Limitations of Mathematical Reasoning in Large Language Models',
    authors: 'Apple AI Research',
    date: '2024',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Demonstrated that LLM performance on complex mathematical reasoning tasks significantly diminished after researchers altered the standard numerical values found in published equations. This definitively proves models heavily rely on pattern matching and retrieval over actual, adaptable symbolic logic.',
  },
  {
    id: 'external-145',
    title: 'Towards More Rigorous LLM Evals',
    sourceUrl: 'https://iclr-blogposts.github.io/2025/blog/towards-more-rigorous-llm-evals/',
    displayTitle: 'Towards More Rigorous LLM Evals',
    authors: 'Miller, et al.',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['llm'],
    abstract: 'Evaluates the reasoning versus pattern-matching debate by injecting "NoOp" (no operation/irrelevant) examples into the context window. Models fail to significantly improve performance when given NoOp contextual clues, indicating a fundamental struggle to perform dynamic, in-context reasoning when distractor information is present.',
  },
  {
    id: 'external-146',
    title: 'AI Model Benchmark Comparison 2024-2025: Performance Analysis',
    sourceUrl: 'https://cisomarketplace.com/blog/ai-model-benchmark-comparison-2024-2025-performance-analysis',
    displayTitle: 'AI Model Benchmark Comparison 2024-2025: Performance Analysis',
    authors: 'CISO Marketplace / Epoch',
    date: '2025',
    status: 'Published',
    category: 'productivity',
    tags: ['ai-research'],
    abstract: 'Evaluates the newly introduced FrontierMath benchmark, revealing stark capability gaps. OpenAI\'s o3 achieved only 25.2% on research-level math, a massive drop from general benchmarks, confirming that true novel reasoning remains elusive. In coding, Claude 3.5 Sonnet achieved 72.7% on SWE-bench Verified.',
  },
  {
    id: 'external-147',
    title: 'Judging LLM-as-a-Judge with MT-Bench and Chatbot Arena',
    sourceUrl: 'https://api.substack.com/feed/podcast/7088450.rss',
    displayTitle: 'Judging LLM-as-a-Judge with MT-Bench and Chatbot Arena',
    authors: 'Zheng, L., et al.',
    date: '2023',
    status: 'Published',
    category: 'productivity',
    tags: ['llm'],
    abstract: 'Critiques the increasingly widespread industry practice of using GPT-4 to grade the outputs of other language models. Highlights the inherent biases and recursive, self-affirming feedback loops created when an AI is utilized as the primary evaluator for benchmarking broader intelligence capabilities.',
  },
];

